import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash, FloppyDisk } from "@phosphor-icons/react";

const empty = { name: "", category: "Assurance & Prévoyance", description: "", logo_url: "", order: 0 };

export default function AdminPartners() {
  const [partners, setPartners] = useState([]);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState(empty);

  const load = () => api.get("/partners").then((r) => setPartners(r.data || []));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!draft.name.trim()) return toast.error("Nom requis");
    try {
      await api.post("/partners", draft);
      toast.success("Partenaire ajouté");
      setDraft(empty);
      setCreating(false);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const save = async (p) => {
    try {
      await api.put(`/partners/${p.id}`, { name: p.name, category: p.category, description: p.description, logo_url: p.logo_url, order: Number(p.order) });
      toast.success("Mis à jour");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Supprimer ${p.name} ?`)) return;
    try {
      await api.delete(`/partners/${p.id}`);
      toast.success("Supprimé");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const inputCls = "w-full bg-ducs-night border border-ducs-gold/25 px-3 py-2 text-ducs-cream rounded-sm text-sm focus:border-ducs-gold";

  return (
    <div data-testid="admin-partners" className="p-8 md:p-10">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="label-caps mb-3">Partenaires</div>
          <h1 className="font-serif text-3xl md:text-4xl text-ducs-cream">Gestion du réseau</h1>
          <p className="text-ducs-mist mt-2">{partners.length} partenaires affichés sur le site.</p>
        </div>
        <button onClick={() => setCreating(true)} data-testid="add-partner-btn" className="btn-primary">
          <Plus size={16} weight="bold" /> Ajouter
        </button>
      </div>

      {creating && (
        <div className="mt-8 card-ducs p-6">
          <h3 className="font-serif text-xl text-ducs-cream mb-4">Nouveau partenaire</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <input placeholder="Nom" className={inputCls} value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} data-testid="partner-name-input" />
            <input placeholder="Catégorie" className={inputCls} value={draft.category} onChange={(e) => setDraft({ ...draft, category: e.target.value })} />
            <input placeholder="URL du logo (optionnel)" className={inputCls} value={draft.logo_url} onChange={(e) => setDraft({ ...draft, logo_url: e.target.value })} />
            <input type="number" placeholder="Ordre" className={inputCls} value={draft.order} onChange={(e) => setDraft({ ...draft, order: Number(e.target.value) })} />
            <textarea placeholder="Description courte" className={`${inputCls} sm:col-span-2`} rows={2} value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
          </div>
          <div className="mt-4 flex gap-3">
            <button onClick={create} className="btn-primary" data-testid="save-partner-btn"><FloppyDisk size={16} /> Enregistrer</button>
            <button onClick={() => { setCreating(false); setDraft(empty); }} className="btn-secondary">Annuler</button>
          </div>
        </div>
      )}

      <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {partners.map((p) => (
          <PartnerCard key={p.id} partner={p} onSave={save} onDelete={remove} />
        ))}
      </div>
    </div>
  );
}

function PartnerCard({ partner, onSave, onDelete }) {
  const [p, setP] = useState(partner);
  useEffect(() => { setP(partner); }, [partner]);
  const inputCls = "w-full bg-ducs-night border border-ducs-gold/25 px-3 py-2 text-ducs-cream rounded-sm text-sm focus:border-ducs-gold";

  return (
    <div className="card-ducs p-5 hover:translate-y-0">
      <div className="flex items-center gap-3 mb-3">
        <div className="h-12 w-20 bg-ducs-night flex items-center justify-center border border-ducs-gold/15 rounded-sm overflow-hidden">
          {p.logo_url ? <img src={p.logo_url} alt={p.name} className="max-h-10 max-w-[72px] object-contain" /> : <span className="text-ducs-gold font-serif text-xs">{p.name.slice(0, 6)}</span>}
        </div>
        <input className={inputCls} value={p.name} onChange={(e) => setP({ ...p, name: e.target.value })} />
      </div>
      <input className={`${inputCls} mb-2`} placeholder="Catégorie" value={p.category || ""} onChange={(e) => setP({ ...p, category: e.target.value })} />
      <input className={`${inputCls} mb-2`} placeholder="URL du logo" value={p.logo_url || ""} onChange={(e) => setP({ ...p, logo_url: e.target.value })} />
      <input type="number" className={`${inputCls} mb-2`} placeholder="Ordre" value={p.order ?? 0} onChange={(e) => setP({ ...p, order: e.target.value })} />
      <textarea className={inputCls} rows={2} placeholder="Description" value={p.description || ""} onChange={(e) => setP({ ...p, description: e.target.value })} />
      <div className="flex items-center justify-between mt-3 pt-3 border-t border-ducs-gold/10">
        <button onClick={() => onSave(p)} className="text-sm text-ducs-gold inline-flex items-center gap-1" data-testid={`save-partner-${p.id}`}>
          <FloppyDisk size={14} /> Enregistrer
        </button>
        <button onClick={() => onDelete(p)} className="text-sm text-ducs-mist hover:text-red-400 inline-flex items-center gap-1" data-testid={`delete-partner-${p.id}`}>
          <Trash size={14} /> Supprimer
        </button>
      </div>
    </div>
  );
}
